/**
 * O DIA QUE SE ESTÁ LANÇANDO — compartilhado pela Ronda e pelo Trato.
 *
 * Até agora as duas telas eram travadas em `hoje()`, e o dia esquecido ficava
 * em branco para sempre: quem não registrou ontem não tinha por onde registrar
 * hoje. Isso transformava qualquer tropeço — celular sem bateria, tratador de
 * folga, sinal ruim a manhã inteira — em buraco permanente no histórico.
 *
 * TRÊS CUIDADOS, porque data errada é o jeito mais fácil de sujar o banco:
 *
 * 1. NÃO EXISTE FUTURO. O máximo é hoje. Lançar trato em data que ainda não
 *    aconteceu não é um caso de uso; é dedo escorregando no teclado.
 *
 * 2. O PASSADO TEM FUNDO. Noventa dias. O limite não protege contra malícia,
 *    protege contra o ano digitado errado — «2025» no lugar de «2026» manda o
 *    registro para um ciclo que já foi encerrado.
 *
 * 3. QUANDO NÃO É HOJE, A TELA INTEIRA AVISA. Em faixa, com o dia por extenso,
 *    e com um botão para voltar. O erro caro não é lançar em outro dia — é
 *    lançar em outro dia SEM PERCEBER, e seguir a ronda toda assim.
 */

export const DIAS_PARA_TRAS = 90

export function hojeISO(): string {
  const d = new Date()
  const m = `${d.getMonth() + 1}`.padStart(2, '0')
  const dia = `${d.getDate()}`.padStart(2, '0')
  return `${d.getFullYear()}-${m}-${dia}`
}

function somarDias(iso: string, n: number): string {
  const [a, m, d] = iso.split('-').map(Number)
  const data = new Date(a ?? 2026, (m ?? 1) - 1, (d ?? 1) + n)
  const mm = `${data.getMonth() + 1}`.padStart(2, '0')
  const dd = `${data.getDate()}`.padStart(2, '0')
  return `${data.getFullYear()}-${mm}-${dd}`
}

export function porExtenso(iso: string): string {
  const [a, m, d] = iso.split('-').map(Number)
  const data = new Date(a ?? 2026, (m ?? 1) - 1, d ?? 1)
  return data.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  })
}

export function SeletorDeDia({
  dia,
  aoMudar,
  trancado,
}: {
  dia: string
  aoMudar: (novo: string) => void
  /**
   * Por que a data não pode mais mudar — dito na tela. Sem motivo, ela muda.
   * Trancar calado seria um seletor que parece quebrado.
   */
  trancado?: string | undefined
}) {
  const hoje = hojeISO()
  const minimo = somarDias(hoje, -DIAS_PARA_TRAS)
  const eHoje = dia === hoje

  return (
    <div className={eHoje ? 'seletor-dia' : 'seletor-dia outro-dia'}>
      <div className="seletor-linha">
        <button
          type="button"
          className="seletor-seta"
          onClick={() => aoMudar(somarDias(dia, -1))}
          disabled={trancado !== undefined || dia <= minimo}
          aria-label="dia anterior"
        >
          ◀
        </button>

        <label className="seletor-campo">
          <span className="campo-rotulo">
            {/* O CAMPO DE DATA FALA A LÍNGUA DO CELULAR, e não a nossa: num
                aparelho configurado em inglês ele escreve 09/18/2026, mês
                primeiro. Por isso o dia aparece por extenso aqui do lado,
                sempre — inclusive hoje. Dia por extenso não tem como ser lido
                ao contrário. */}
            {porExtenso(dia)}
            {eHoje && <span className="seletor-hoje"> · hoje</span>}
          </span>
          <input
            type="date"
            value={dia}
            min={minimo}
            max={hoje}
            disabled={trancado !== undefined}
            onChange={(e) => {
              const novo = e.target.value
              if (novo >= minimo && novo <= hoje) aoMudar(novo)
            }}
          />
        </label>

        <button
          type="button"
          className="seletor-seta"
          onClick={() => aoMudar(somarDias(dia, 1))}
          disabled={trancado !== undefined || eHoje}
          aria-label="dia seguinte"
        >
          ▶
        </button>
      </div>

      {!eHoje && (
        <p className="seletor-aviso">
          Você está lançando em <strong>{porExtenso(dia)}</strong>, e não hoje.
          {trancado === undefined && (
            <button type="button" className="discreto" onClick={() => aoMudar(hoje)}>
              voltar para hoje
            </button>
          )}
        </p>
      )}
      {trancado !== undefined && <p className="campo-dica">{trancado}</p>}
    </div>
  )
}
